import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Store, Tags, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { cn } from "@/lib/utils";

export default function Shopping() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState("category");
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [store, setStore] = useState("");
  const [category, setCategory] = useState("");

  const load = useCallback(async () => {
    try {
      setItems(await api("/shopping"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    const body = {
      name: name.trim(),
      quantity: qty.trim(),
      store: store.trim() || "Any Store",
      category: category.trim() || "General",
      checked: false,
    };
    setName("");
    setQty("");
    try {
      const created = await api("/shopping", { method: "POST", body });
      setItems((prev) => [created, ...prev]);
    } catch {
      toast.error("Could not add item");
    }
  };

  const toggle = async (item) => {
    const next = { ...item, checked: !item.checked };
    setItems((prev) => prev.map((x) => (x.id === item.id ? next : x)));
    try {
      await api(`/shopping/${item.id}`, {
        method: "PUT",
        body: {
          name: item.name,
          category: item.category,
          store: item.store,
          quantity: item.quantity,
          checked: next.checked,
          meal_id: item.meal_id,
        },
      });
    } catch {
      load();
    }
  };

  const remove = async (item) => {
    setItems((prev) => prev.filter((x) => x.id !== item.id));
    try {
      await api(`/shopping/${item.id}`, { method: "DELETE" });
    } catch {
      load();
    }
  };

  const clearDone = async () => {
    try {
      await api("/shopping/clear-completed", { method: "POST" });
      await load();
      toast.success("Cleared checked items");
    } catch {
      toast.error("Could not clear");
    }
  };

  const groups = useMemo(() => {
    const key = groupBy === "store" ? "store" : "category";
    const map = {};
    items.forEach((it) => {
      const g = it[key] || (groupBy === "store" ? "Any Store" : "General");
      if (!map[g]) map[g] = [];
      map[g].push(it);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items, groupBy]);

  const doneCount = items.filter((i) => i.checked).length;

  return (
    <div>
      <PageHeader
        title="Shopping"
        subtitle={`${items.length - doneCount} to buy · ${doneCount} in cart`}
        action={
          doneCount > 0 && (
            <Button variant="outline" className="rounded-full" onClick={clearDone} data-testid="clear-completed-btn">
              Clear checked
            </Button>
          )
        }
      />

      {/* Add bar */}
      <div className="bento-tile mt-4 flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add an item…"
          className="rounded-xl border-0 bg-secondary/60 sm:flex-1"
          data-testid="shopping-name-input"
        />
        <Input
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Qty"
          className="rounded-xl border-0 bg-secondary/60 sm:w-24"
          data-testid="shopping-qty-input"
        />
        <Input
          value={groupBy === "store" ? store : category}
          onChange={(e) => (groupBy === "store" ? setStore(e.target.value) : setCategory(e.target.value))}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={groupBy === "store" ? "Store" : "Category"}
          className="rounded-xl border-0 bg-secondary/60 sm:w-36"
          data-testid="shopping-group-input"
        />
        <Button className="rounded-xl" onClick={add} data-testid="shopping-add-btn">
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Group toggle */}
      <div className="mt-4 inline-flex rounded-full bg-secondary p-1">
        <button
          onClick={() => setGroupBy("category")}
          data-testid="group-by-category"
          className={cn(
            "flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
            groupBy === "category" ? "bg-card text-ink shadow-sm" : "text-muted-foreground"
          )}
        >
          <Tags className="h-4 w-4" /> Category
        </button>
        <button
          onClick={() => setGroupBy("store")}
          data-testid="group-by-store"
          className={cn(
            "flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
            groupBy === "store" ? "bg-card text-ink shadow-sm" : "text-muted-foreground"
          )}
        >
          <Store className="h-4 w-4" /> Store
        </button>
      </div>

      {loading ? (
        <div className="mt-4 space-y-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-3xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState emoji="🛒" title="Nothing on the list" message="Add items above and group them by store or category." />
      ) : (
        <div className="mt-4 space-y-5">
          {groups.map(([groupName, groupItems]) => (
            <div key={groupName} data-testid={`shopping-group-${groupName}`}>
              <h2 className="mb-2 px-1 font-display text-base font-semibold text-muted-foreground">{groupName}</h2>
              <div className="bento-tile divide-y divide-border/70 p-1">
                {groupItems.map((it) => (
                  <div key={it.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`shopping-item-${it.id}`}>
                    <Checkbox
                      checked={it.checked}
                      onCheckedChange={() => toggle(it)}
                      data-testid={`shopping-check-${it.id}`}
                    />
                    <div className={cn("min-w-0 flex-1", it.checked && "opacity-45 line-through")}>
                      <span className="font-semibold text-ink">{it.name}</span>
                      {it.quantity && <span className="ml-2 text-sm text-muted-foreground">{it.quantity}</span>}
                    </div>
                    <button
                      onClick={() => remove(it)}
                      className="text-muted-foreground/60 hover:text-destructive"
                      data-testid={`shopping-delete-${it.id}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
